import { defineStore } from 'pinia'
import { type Role } from '@/services/models'

export interface IngestMetaState {
  firstTransactionDate: string
  lastTransactionDate: string
}

export interface FilterState {
  currentRole: Role | null
  roles: Role[]
  startDate: string
  endDate: string
  ingestMeta: IngestMetaState | null
  // Top-bar branch / relationship-manager scope: exact Account values, any-of
  // within a list, both lists combined. Empty lists mean every account. Cleared
  // on bank switch because the names are tenant-specific.
  branches: string[]
  relationshipManagers: string[]
  // Monotonic counter bumped once whenever a filter change should refresh the
  // dashboard (bank switch, date-range or scope change). Subscribers key off
  // this so a refresh fires even when the underlying values (e.g. the date
  // range) end up identical after switching banks.
  revision: number
}


export const useDashboardFiltersStore = defineStore('dashboardFilters', {
  state: (): FilterState => ({
    currentRole: null,
    roles: [],
    startDate: "",
    endDate: "",
    ingestMeta: null,
    branches: [],
    relationshipManagers: [],
    revision: 0
  }),
  actions: {
    setRole(role: Role) {
      this.currentRole = role
    },
    setRoles(roles: Role[]) {
      this.roles = roles
    },
    setDateRange(range: { start: string; end: string }) {
      this.startDate = range.start
      this.endDate = range.end
    },
    setIngestMeta(meta: IngestMetaState | null) {
      this.ingestMeta = meta
    },
    setScope(scope: { branches: string[]; relationshipManagers: string[] }) {
      this.branches = scope.branches
      this.relationshipManagers = scope.relationshipManagers
    },
    bumpRevision() {
      this.revision++
    },
    reset() {
      // Keep current role if available, but clear everything else
      this.roles = []
      this.startDate = ""
      this.endDate = ""
      this.ingestMeta = null
      this.branches = []
      this.relationshipManagers = []
    }
  },
  persist: {
    key: 'dashboardFilters',
    storage: localStorage
  }
})
